import * as React from 'react'

import cn from '@config/tailwind/cn'

export interface LoadingProps {
  className?: string
  withDottedBackground?: boolean
}

const PIBO_LOGO_ANIMATION_CSS = `
  .pibo-logo-svg {
    width: 100px;
    height: auto;
    overflow: visible;
    transform-box: fill-box;
    transform-origin: center;
    animation:
      pibo-pop 0.7s cubic-bezier(.34, 1.56, .64, 1) both,
      pibo-idle 1.8s ease-in-out 1.6s infinite;
  }
  @keyframes pibo-pop {
    0%   { transform: scale(0) rotate(-14deg); opacity: 0; }
    60%  { transform: scale(1.08) rotate(3deg); opacity: 1; }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }
  @keyframes pibo-idle {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.09); }
  }
  .pibo-logo-svg .pibo-badge {
    transform-box: fill-box;
    transform-origin: center;
    animation: pibo-badge-in 0.55s cubic-bezier(.34, 1.56, .64, 1) both;
  }
  @keyframes pibo-badge-in {
    0%   { transform: scale(0.4); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  .pibo-logo-svg .pibo-letter {
    transform-box: fill-box;
    transform-origin: center;
    opacity: 0;
    animation:
      pibo-letter-in 0.5s cubic-bezier(.34, 1.56, .64, 1) both,
      pibo-letter-pulse 2s ease-in-out infinite;
  }
  @keyframes pibo-letter-in {
    0%   { transform: translateY(10px) scale(0.6); opacity: 0; }
    100% { transform: translateY(0) scale(1); opacity: 1; }
  }
  @keyframes pibo-letter-pulse {
    0%, 35%, 100% { transform: translateY(0) scale(1); }
    15%           { transform: translateY(-3px) scale(1.18); }
  }
  /* delays: pibo-letter-in (intro stagger), pibo-letter-pulse (looping wave) */
  .pibo-logo-svg .pibo-l1 { animation-delay: 0.55s, 1.7s; }
  .pibo-logo-svg .pibo-l4 { animation-delay: 0.68s, 1.85s; }
  .pibo-logo-svg .pibo-l2 { animation-delay: 0.81s, 2s; }
  .pibo-logo-svg .pibo-l3 { animation-delay: 0.94s, 2.15s; }
  @media (prefers-reduced-motion: reduce) {
    .pibo-logo-svg,
    .pibo-logo-svg .pibo-badge,
    .pibo-logo-svg .pibo-letter {
      animation: none;
      opacity: 1;
    }
  }
`

function PiboLogoAnimation() {
  return (
    <svg
      className='pibo-logo-svg'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 83.1 66'
      role='img'
      aria-label='PiBo'
    >
      <path
        className='pibo-badge'
        fill='#e73957'
        d='M42,0C43.5.05,45,.07,46.55.15A50.48,50.48,0,0,1,60.38,2.64,36.88,36.88,0,0,1,72.73,9.51a34.81,34.81,0,0,1,6.32,6.9,24.28,24.28,0,0,1,3.48,8.45,27.19,27.19,0,0,1,.53,7.08,35.88,35.88,0,0,1-1.32,7.62,36.34,36.34,0,0,1-4.07,9.53,26.83,26.83,0,0,1-7.44,7.83A44.09,44.09,0,0,1,56.8,63.33a65.31,65.31,0,0,1-7.94,1.85c-1.59.26-3.19.43-4.8.61a42.6,42.6,0,0,1-5.87.18,44.26,44.26,0,0,1-22.25-6.63,38.86,38.86,0,0,1-7-5.36,30.3,30.3,0,0,1-7.82-13.4,28.31,28.31,0,0,1-1-5.47,27.66,27.66,0,0,1,0-4.55A29.8,29.8,0,0,1,6.76,14a34.11,34.11,0,0,1,12.62-9.5,48.16,48.16,0,0,1,10.26-3.3A48.48,48.48,0,0,1,35,.35C36.29.22,37.6.14,38.91.09c1,0,2,0,3.06,0Z'
      />
      <g className='pibo-letter pibo-l1' fill='#fff'>
        <path d='M30.11,28.49A9.33,9.33,0,0,1,26,36.31a15.7,15.7,0,0,1-4.65,2.36c-.21.07-.27.15-.22.38.32,1.7.64,3.41.92,5.12a2.11,2.11,0,0,1-.92,2.15,4.85,4.85,0,0,1-2,.76c-.83.16-1.65.33-2.48.47a3.48,3.48,0,0,1-1,0,1.31,1.31,0,0,1-1.13-1.22c-.44-2.45-.89-4.91-1.33-7.37q-.63-3.44-1.25-6.87c-.37-2-.73-4.06-1.11-6.08-.24-1.31-.5-2.61-.74-3.91a4.13,4.13,0,0,1-.08-.78,1,1,0,0,1,.87-1.07c1.52-.34,3-.67,4.56-1,1.14-.24,2.27-.47,3.41-.66a11.11,11.11,0,0,1,5.69.35,6.9,6.9,0,0,1,3.27,2.51,11.12,11.12,0,0,1,2.12,5.14C30,27.24,30.05,27.87,30.11,28.49Zm-8.74.23c0-.21,0-.42-.05-.63a6.19,6.19,0,0,0-.55-2,2.47,2.47,0,0,0-1.59-1.34,1.63,1.63,0,0,0-1.37.39c-.28.19-.14.52-.15.8a1.94,1.94,0,0,0,0,.24,25.07,25.07,0,0,0,1,3.9,1.06,1.06,0,0,0,1.75.59A2.07,2.07,0,0,0,21.37,28.72Z' />
        <path
          fill='#e73957'
          d='M21.37,28.72a2.07,2.07,0,0,1-.94,1.93,1.06,1.06,0,0,1-1.75-.59,25.07,25.07,0,0,1-1-3.9,1.94,1.94,0,0,1,0-.24c0-.28-.13-.61.15-.8a1.63,1.63,0,0,1,1.37-.39,2.47,2.47,0,0,1,1.59,1.34,6.19,6.19,0,0,1,.55,2C21.34,28.3,21.35,28.51,21.37,28.72Z'
        />
      </g>
      <g className='pibo-letter pibo-l2' fill='#fff'>
        <path d='M51.09,30.68c.54.13,1.06.22,1.56.38a5.24,5.24,0,0,1,3.93,4.76,9.59,9.59,0,0,1-1.95,7A5.43,5.43,0,0,1,50.36,45c-1.44,0-2.89,0-4.33-.09-1.14-.07-2.27-.25-3.4-.39A2,2,0,0,1,41,43.23a11.42,11.42,0,0,1-.76-2.82,42.94,42.94,0,0,1-.49-6.51c0-2,0-4.06,0-6.09,0-1.85.15-3.69.24-5.53a8.53,8.53,0,0,1,.19-1.6,3.41,3.41,0,0,1,3.31-2.53c1.11,0,2.22,0,3.33.06a17.76,17.76,0,0,1,6,1.21,8.17,8.17,0,0,1,.93.48,3.78,3.78,0,0,1,2,3.42,8.74,8.74,0,0,1-.45,3.13,7.29,7.29,0,0,1-3.6,3.93C51.52,30.49,51.31,30.58,51.09,30.68Zm-4.46,4.84a11.85,11.85,0,0,0,.2,1.31c.2.78.74,1,1.44.6a2.92,2.92,0,0,0,1.59-2.32,2.36,2.36,0,0,0-1.68-2.82c-.83-.26-1.16-.11-1.29.75S46.71,34.68,46.63,35.52Zm0-7.76a6.36,6.36,0,0,0,.07.7c.12.64.55,1,1.08.94a2.63,2.63,0,0,0,1.51-.73,1.69,1.69,0,0,0-.49-2.85,5.8,5.8,0,0,0-1.17-.33c-.42-.08-.63.07-.78.48a1.55,1.55,0,0,0-.06.23C46.72,26.74,46.67,27.27,46.62,27.76Z' />
        <path
          fill='#e73957'
          d='M46.63,35.52c.08-.84.13-1.66.26-2.48s.46-1,1.29-.75a2.36,2.36,0,0,1,1.68,2.82,2.92,2.92,0,0,1-1.59,2.32c-.7.38-1.24.18-1.44-.6A11.85,11.85,0,0,1,46.63,35.52Z'
        />
        <path
          fill='#e73957'
          d='M46.62,27.76c.05-.49.1-1,.16-1.56a1.55,1.55,0,0,1,.06-.23c.15-.41.36-.56.78-.48a5.8,5.8,0,0,1,1.17.33,1.69,1.69,0,0,1,.49,2.85,2.63,2.63,0,0,1-1.51.73c-.53.08-1-.3-1.08-.94A6.36,6.36,0,0,1,46.62,27.76Z'
        />
      </g>
      <g className='pibo-letter pibo-l3' fill='#fff'>
        <path d='M75.1,35.17a17.08,17.08,0,0,1-1.13,6,8.47,8.47,0,0,1-6.26,5.26A6.85,6.85,0,0,1,61.2,44.6,10.38,10.38,0,0,1,58.12,39a13.88,13.88,0,0,1-.29-3.8,12.12,12.12,0,0,1,1.84-5.93,7.7,7.7,0,0,1,5.44-3.57,8.33,8.33,0,0,1,5.41.82,8.05,8.05,0,0,1,2.75,2.57A10,10,0,0,1,75,34C75.05,34.42,75.07,34.86,75.1,35.17ZM68,34a4.37,4.37,0,0,0-.43-2.12,1.92,1.92,0,0,0-3.3-.36,5.26,5.26,0,0,0-1.07,2.59,3.69,3.69,0,0,0,.35,2.33,2.11,2.11,0,0,0,3.7.12A5.32,5.32,0,0,0,68,34Z' />
        <path
          fill='#e73957'
          d='M68,34a5.32,5.32,0,0,1-.75,2.56,2.11,2.11,0,0,1-3.7-.12,3.69,3.69,0,0,1-.35-2.33,5.26,5.26,0,0,1,1.07-2.59,1.92,1.92,0,0,1,3.3.36A4.37,4.37,0,0,1,68,34Z'
        />
      </g>
      <g className='pibo-letter pibo-l4' fill='#fff'>
        <path d='M37.2,39.94v5.37a2.06,2.06,0,0,1-1.63,2.24,4,4,0,0,1-2.91-.18,2,2,0,0,1-1.33-1.81c0-.5,0-1,.05-1.51s.06-1.21.08-1.81l.21-6.52c0-.4,0-.81,0-1.22a2,2,0,0,1,.9-1.73,3.17,3.17,0,0,1,3.49-.2,1.91,1.91,0,0,1,1.07,1.84c0,1.84,0,3.69,0,5.53Z' />
        <path d='M34.3,24.5A2.8,2.8,0,0,1,36,25a2.21,2.21,0,0,1,.62,2.67,2.27,2.27,0,0,1-1.71,1.42,2.47,2.47,0,0,1-3.08-1.55,2.3,2.3,0,0,1,1.89-3Z' />
      </g>
    </svg>
  )
}

export function Loading({ className, withDottedBackground = true }: LoadingProps) {
  return (
    <div
      className={cn(
        'srd:h-dvh srd:w-full srd:flex srd:items-center srd:justify-center srd:select-none srd:overflow-hidden srd:bg-neutrals-white srd:relative',
        className,
      )}
    >
      {withDottedBackground && (
        <div
          className={cn(
            'srd:absolute srd:inset-0',
            'srd:bg-size-[20px_20px]',
            'srd:bg-[radial-gradient(#e73957_1px,transparent_1px)]',
            'srd:dark:bg-[radial-gradient(#e73957_1px,transparent_1px)]',
            'srd:z-1 srd:opacity-30',
          )}
        />
      )}

      <style>{PIBO_LOGO_ANIMATION_CSS}</style>

      <div className='srd:w-fit srd:h-fit srd:flex srd:items-center srd:justify-center srd:gap-2 srd:z-10'>
        <PiboLogoAnimation />
      </div>
    </div>
  )
}
